<template>
  <div class="fs fs-multi" :class="{ disabled }" ref="root">
    <button class="fs-btn" type="button" :disabled="disabled" @click="toggle">
      <span v-if="icon" class="fs-icon" aria-hidden="true">{{ icon }}</span>
      <span class="fs-text" :class="{ placeholder: !summaryText }">
        {{ summaryText || placeholder }}
      </span>
      <span class="fs-chevron" aria-hidden="true">▾</span>
    </button>

    <Transition name="fs-pop">
      <div v-if="open" class="fs-pop">
        <div v-if="searchable" class="fs-search">
          <span class="fs-search-ico" aria-hidden="true">⌕</span>
          <input v-model="q" class="fs-search-input" :placeholder="searchPlaceholder" />
        </div>
        <div class="fs-list" role="listbox" :aria-multiselectable="true">
          <label
            v-for="opt in filtered"
            :key="String(opt.value)"
            class="fs-item fs-item--multi"
            :class="{ checked: isChecked(opt), disabled: opt.disabled }"
          >
            <input
              type="checkbox"
              class="fs-item-cb"
              :checked="isChecked(opt)"
              :disabled="disabled || opt.disabled"
              @change="onToggle(opt, $event)"
              @click.stop
            />
            <span class="fs-item-label">{{ opt.label }}</span>
            <span v-if="showValue" class="fs-item-value">{{ opt.value }}</span>
          </label>
          <div v-if="!filtered.length" class="fs-empty">无匹配项</div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  /** 选中的 value 列表（如会员 id） */
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: '请选择' },
  icon: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  searchable: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: '搜索…' },
  showValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'change'])

const open = ref(false)
const q = ref('')
const root = ref(null)

function normIds(arr) {
  const s = new Set()
  for (const x of arr || []) {
    const n = Number(x)
    if (Number.isFinite(n) && n > 0) s.add(n)
  }
  return Array.from(s).sort((a, b) => a - b)
}

const idSet = computed(() => new Set(normIds(props.modelValue)))

const summaryText = computed(() => {
  const ids = normIds(props.modelValue)
  if (!ids.length) return ''
  const labels = ids.map((id) => {
    const hit = (props.options || []).find((o) => Number(o.value) === id)
    return hit ? String(hit.label || '') : `#${id}`
  })
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]}、${labels[1]}`
  return `${labels[0]}、${labels[1]} 等 ${labels.length} 人`
})

const filtered = computed(() => {
  const list = props.options || []
  const query = (q.value || '').trim().toLowerCase()
  if (!props.searchable || !query) return list
  return list.filter((o) => {
    const a = String(o.label || '').toLowerCase()
    const b = String(o.value ?? '').toLowerCase()
    return a.includes(query) || b.includes(query)
  })
})

function isChecked(opt) {
  const v = Number(opt && 'value' in opt ? opt.value : NaN)
  return Number.isFinite(v) && v > 0 && idSet.value.has(v)
}

function onToggle(opt, e) {
  if (opt && opt.disabled) return
  const v = Number(opt && 'value' in opt ? opt.value : NaN)
  if (!Number.isFinite(v) || v <= 0) return
  const want = e && e.target ? !!e.target.checked : !isChecked(opt)
  const cur = new Set(normIds(props.modelValue))
  if (want) cur.add(v)
  else cur.delete(v)
  const next = Array.from(cur).sort((a, b) => a - b)
  emit('update:modelValue', next)
  emit('change', next)
}

function close() {
  open.value = false
  q.value = ''
}

function toggle() {
  if (props.disabled) return
  open.value = !open.value
  if (!open.value) q.value = ''
}

function onDocClick(e) {
  const el = root.value
  if (!el) return
  if (!el.contains(e.target)) close()
}

watch(open, async (v) => {
  document.removeEventListener('click', onDocClick)
  if (!v) return
  await nextTick()
  if (open.value) document.addEventListener('click', onDocClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick)
})
</script>

<style scoped>
.fs {
  position: relative;
  width: 100%;
}
.fs.disabled {
  opacity: 0.65;
  pointer-events: none;
}
.fs-btn {
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #d0d7e2;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  cursor: pointer;
}
.fs-btn:hover {
  border-color: #c5d3ee;
  box-shadow: 0 6px 18px rgba(47, 106, 246, 0.08);
}
.fs-icon {
  opacity: 0.8;
}
.fs-text {
  flex: 1;
  text-align: left;
  color: #1d2939;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fs-text.placeholder {
  color: #98a2b3;
}
.fs-chevron {
  color: #667085;
  font-size: 14px;
}

.fs-pop {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  background: #fff;
  border: 1px solid #e9eef5;
  border-radius: 12px;
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.18);
  padding: 8px;
  z-index: 50;
}
.fs-pop-enter-active {
  transition: opacity 0.16s ease, transform 0.16s cubic-bezier(0.22, 0.61, 0.36, 1);
  transform-origin: top;
}
.fs-pop-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
  transform-origin: top;
}
.fs-pop-enter-from,
.fs-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}
.fs-pop-enter-to,
.fs-pop-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.fs-search {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #eef2f7;
  background: #fbfcff;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 8px;
}
.fs-search-ico {
  color: #667085;
}
.fs-search-input {
  border: none;
  outline: none;
  background: transparent;
  flex: 1;
}
.fs-list {
  max-height: 260px;
  overflow: auto;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}
.fs-list::-webkit-scrollbar {
  width: 8px;
}
.fs-list::-webkit-scrollbar-track {
  background: transparent;
}
.fs-list::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.7);
}
.fs-list::-webkit-scrollbar-thumb:hover {
  background: rgba(15, 23, 42, 0.24);
}
.fs-item--multi {
  width: 100%;
  border: 1px solid transparent;
  background: #fff;
  border-radius: 10px;
  padding: 8px 10px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s ease, border-color 0.12s ease;
  margin: 0;
  box-sizing: border-box;
}
.fs-item--multi:hover {
  background: #f5f8ff;
  border-color: #d8e4ff;
}
.fs-item--multi.checked {
  background: #eef4ff;
  border-color: #c5d3ee;
}
.fs-item--multi.disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}
.fs-item-cb {
  margin-top: 2px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
}
.fs-item-label {
  font-weight: 600;
  font-size: 13px;
  flex: 1;
  min-width: 0;
  line-height: 1.35;
}
.fs-item-value {
  font-size: 12px;
  color: #667085;
  margin-left: 6px;
  flex-shrink: 0;
}
.fs-empty {
  padding: 10px;
  color: #667085;
  font-size: 13px;
}
</style>
